/**
 * @file src/_modulos/prenota/tabela/layouts/columns.tsx
 * @description Colunas simplificadas da tabela de pré-notas
 *
 * Princípios aplicados:
 * - KISS: Lógica simples e direta
 * - SRP: Apenas define colunas
 * - YAGNI: Remove animações complexas não utilizadas
 */

"use client";

import { ColumnDef } from "@tanstack/react-table";
import { fmt } from "visuais";
import {
  PrenotaRow,
  DataTableColumnHeader,
  getPriorityPreset,
  getStatusPreset,
  getVencimentoPreset,
  BadgeWithTooltip,
  TipoCell,
  UserCell,
  ActionsCell,
} from "@PN/tabela";
import { COLUMN_SIZES, CELL_CLASSES } from "./components/utils";

/**
 * Definição das colunas
 */
export const columns: ColumnDef<PrenotaRow>[] = [
  {
    accessorKey: "F1_XUSRRA",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Usuário" />
    ),
    cell: ({ row }) => (
      <div className={CELL_CLASSES.centered}>
        <UserCell row={row} />
      </div>
    ),
    ...COLUMN_SIZES.small,
  },

  {
    accessorKey: "F1_XTIPO",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Tipo" />
    ),
    cell: ({ row }) => <TipoCell row={row} />,
    ...COLUMN_SIZES.medium,
  },

  {
    accessorKey: "F1_DOC",
    id: "F1_DOC",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Nota Fiscal" />
    ),
    cell: ({ row }) => (
      <div className={CELL_CLASSES.column}>
        <span className={CELL_CLASSES.responsiveText}>
          {`${row.original.F1_DOC} / ${row.original.F1_SERIE}`}
        </span>
        <span
          className={`${CELL_CLASSES.responsiveText} text-muted-foreground`}
        >
          Emitido: {fmt.dates.BR(row.original.F1_EMISSAO)}
        </span>
      </div>
    ),
    size: 110,
    minSize: 90,
  },

  {
    accessorKey: "A2_NOME",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Fornecedor" />
    ),
    cell: ({ row }) => (
      <div className={CELL_CLASSES.columnText}>
        <span
          className={`${CELL_CLASSES.responsiveText} whitespace-normal break-words leading-snug`}
        >
          {row.original.A2_NOME}
        </span>
        <span
          className={`${CELL_CLASSES.responsiveText} text-muted-foreground mt-0.5`}
        >
          Cod: {row.original.A2_COD} – Loja: {row.original.A2_LOJA}
        </span>
      </div>
    ),
    ...COLUMN_SIZES.large,
  },

  {
    accessorKey: "Z05_DATA",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Inclusão" />
    ),
    cell: ({ row }) => {
      const data = row.original.Z05_DATA;
      const hora = row.original.Z05_HORA;
      return (
        <div className={CELL_CLASSES.column}>
          <div className={CELL_CLASSES.responsiveText}>
            {fmt.dates.BR(data)}
          </div>
          {hora && (
            <div
              className={`${CELL_CLASSES.responsiveText} text-muted-foreground`}
            >
              {hora}
            </div>
          )}
        </div>
      );
    },
    size: 100,
    minSize: 80,
  },
  {
    accessorKey: "F1_VALBRUT",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Valor (R$)" />
    ),
    cell: ({ getValue }) => (
      <div
        className={`${CELL_CLASSES.centered} ${CELL_CLASSES.responsiveText}`}
      >
        {fmt.numbers.currency(getValue() as number)}
      </div>
    ),
    size: 100,
    minSize: 80,
  },
  {
    accessorKey: "VENCIMENTO",
    id: "VENCIMENTO",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Vencimento" />
    ),
    cell: ({ row }) => (
      <div className={CELL_CLASSES.centered}>
        <BadgeWithTooltip
          preset={getVencimentoPreset(
            row.original.VENCIMENTO,
            row.original.E2_BAIXA,
            row.original.E2_SALDO
          )}
        />
      </div>
    ),
    size: 100,
    minSize: 80,
  },
  {
    accessorKey: "F1_XPRIOR",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Prioridade" />
    ),
    cell: ({ row }) => {
      const preset = getPriorityPreset(row.original.F1_XPRIOR?.trim() || "");
      return (
        <div className={CELL_CLASSES.centered}>
          <BadgeWithTooltip
            preset={preset}
            text={row.original.F1_XPRIOR?.trim() || "N/A"}
          />
        </div>
      );
    },
    size: 90,
    minSize: 70,
  },

  {
    accessorKey: "Status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const statusPreset = getStatusPreset(row.original.Status?.trim() || "");
      return (
        <div className={`${CELL_CLASSES.column} gap-1`}>
          <BadgeWithTooltip
            preset={statusPreset}
            text={row.original.Status?.trim() || "N/A"}
          />
        </div>
      );
    },
    size: 90,
    minSize: 70,
  },
  {
    id: "actions",
    header: () => <span className={CELL_CLASSES.centered}>Ações</span>,
    cell: ({ row }) => <ActionsCell row={row} />,
    ...COLUMN_SIZES.actions,
  },
];
